import React, { forwardRef, useEffect, useState } from 'react'

import uploadApi from '_api/upload'
import userApi from '_api/user'
import { SINGAPORE_CODE } from '_utils/constant'
import { generatePayNowStr } from '_utils/paynow'
import PropTypes from 'prop-types'
import QRCode from 'qrcode'
import { AiOutlineCloudDownload } from 'react-icons/ai'
import { FiArrowRight, FiRefreshCw, FiAlertCircle } from 'react-icons/fi'
import { FaCheckCircle } from 'react-icons/fa'
import { useToasts } from 'react-toast-notifications'
import { Spinner } from 'reactstrap'
import hitpayLogo from '_images/hitpay.png'
import useFetchHitpayQr from '../../hooks/useFetchHitpayQr'
import './Payment.scss'

const Payment = forwardRef((props, ref) => {
  const {
    paymentFileUrl,
    confirmPayment,
    final,
    phone,
    hostId,
    pickupTime,
    isSubmittedPayment,
    stringRefNumber = '',
    setPaymentFileUrl,
    isQrLoading = false,
    isPaymentViaHitPay,
    listCheckedOrder,
    orderRef,
  } = props

  const { addToast } = useToasts()
  const [src, setSrc] = useState('')
  const ePickupTime = pickupTime.split(' ')
  const ePickupDate = ePickupTime[0].split('-')
  const [payNowCode, setPayNowCode] = useState('')
  const [space, setSpace] = useState('')
  const [photoUrl, setPhotoUrl] = useState(paymentFileUrl)
  const [host, setHost] = useState(null)
  const [rand, setRand] = useState(0) // Spaces number
  const [isGeneratingQr, setIsGeneratingQr] = useState(true)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const isPayTogether = listCheckedOrder && listCheckedOrder.length > 1

  const {
    hitpayUrl,
    isHitpayLoading,
    isHitpayCompleted,
    isHitpayFailed,
    hitpayReference,
    fetchHitpayQr,
  } = useFetchHitpayQr({
    isPaymentViaHitPay,
    isPayTogether,
    listCheckedOrder,
    orderRef,
    final,
  })

  const isHitpayFallback = Boolean(
    isPaymentViaHitPay &&
    (isHitpayFailed || (!isHitpayLoading && !hitpayUrl && !isHitpayCompleted && !isPayTogether))
  )
  const isShowHitpay = isPaymentViaHitPay && !isHitpayFallback

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { msgResp } = await userApi.getShortUserById(hostId)
        setHost(msgResp)
      } catch (error) {
        console.error('Failed to fetch user:', error)
      }
    }
    fetchData()
  }, [])

  // eslint-disable-next-line radix
  const expiryDate = (parseInt(ePickupDate[2]) + 1)?.toString() + ePickupDate[1] + ePickupDate[0]

  const options = {
    editable: 0,
    expiry: expiryDate,
    amount: final ? Number(final.toFixed(2)) : 0,
    refNumber: `${stringRefNumber}${space}`,
    uen: `${SINGAPORE_CODE}${(phone || '').replace(`${SINGAPORE_CODE}`, '')}`
  }

  if (host?.isPayWithUEN) {
    options.uen = host.uen // 202130996W
  }

  const onUserImageUploaded = async (_event) => {
    const { files } = _event.target
    if (files) {
      const { msgResp: res } = await uploadApi.uploadPhoto(files)
      const { url } = res
      setPhotoUrl(url)
      ref.current = url
      setPaymentFileUrl(url)
    }
  }

  const onReloadQRCode = () => {
    const newRand = (rand + 1) % 5
    setRand(newRand)
    setSpace(' '.repeat(newRand))
  }

  const onDownloadQRCode = () => {
    const qrCodeOptions = {
      errorCorrectionLevel: 'H',
      type: 'image/png',
      color: {
        dark: '#981c84',
      },
    }

    QRCode.toDataURL(payNowCode, qrCodeOptions)
      .then((dataUrl) => {
        const a = document.createElement('a')
        a.href = dataUrl
        a.download = `${options.refNumber}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      })
      .catch((error) => {
        addToast(error.message, {
          appearance: 'error',
          autoDismiss: true,
        })
      })
  }

  useEffect(() => {
    let isMounted = true

    if (!isPaymentViaHitPay || isHitpayFallback) {
      const nextPayNowCode = generatePayNowStr(options, host?.isPayWithUEN)

      setPayNowCode(nextPayNowCode)
      setIsGeneratingQr(true)

      QRCode.toDataURL(nextPayNowCode, {
        errorCorrectionLevel: 'H',
        color: {
          dark: '#981c84',
        },
      })
        .then((dataUrl) => {
          if (!isMounted) return
          setSrc(dataUrl)
        })
        .finally(() => {
          if (!isMounted) return
          setIsGeneratingQr(false)
        })
    }

    return () => {
      isMounted = false
    }
  // eslint-disable-next-line max-len
  }, [space, options.amount, host, stringRefNumber, isPaymentViaHitPay, isHitpayFallback])

  const shouldShowQrOverlay = isQrLoading || isGeneratingQr || !host || !src

  return (
    <div>
      <div id='allowPaymentHtml' style={{ display: 'block' }}>
        <div style={{ height: '16px' }}></div>
        <div id='pendingPaymentHtmlBlock' className='row-bot' style={{ display: 'block' }}>
          <div className='group-list group-list--event detail-event'>
            {isShowHitpay ? (
              <div className='hitpay-checkout-card'>
                <div className='hitpay-card-header'>
                  <div className='hitpay-brand-wrapper'>
                    <div className='hitpay-brand-icon-box'>
                      <img src={hitpayLogo} alt='HitPay' />
                    </div>
                    <div className='hitpay-brand-details'>
                      <span className='hitpay-brand-name'>HitPay Payment</span>
                      <span className='hitpay-brand-desc'>Singapore Online Gateway</span>
                    </div>
                  </div>
                </div>

                <div className='hitpay-summary-box'>
                  <div className='hitpay-summary-row'>
                    <span className='hitpay-label'>Total Payable</span>
                    <span className='hitpay-amount'>${final?.toFixed(2)}</span>
                  </div>
                  {(stringRefNumber || orderRef) && (
                    <div className='hitpay-summary-row'>
                      <span className='hitpay-label'>Order Reference</span>
                      <span className='hitpay-ref-code'>{stringRefNumber || orderRef}</span>
                    </div>
                  )}
                </div>

                {isHitpayCompleted ? (
                  <div className='hitpay-status-card'>
                    <div className='hitpay-success-icon-wrap'>
                      <FaCheckCircle size={30} />
                    </div>
                    <h4 className='hitpay-status-title'>Payment Completed!</h4>
                    <p className='hitpay-status-desc'>
                      Your payment has been received and verified successfully via HitPay.
                    </p>
                    {hitpayReference ? (
                      <span className='hitpay-ref-display'>
                        Ref: {hitpayReference}
                      </span>
                    ) : null}
                  </div>
                ) : (isHitpayLoading || isRedirecting) ? (
                  <div className='hitpay-status-card'>
                    <div className='hitpay-loading-ring'>
                      <Spinner style={{ color: '#009a97', width: '3.2rem', height: '3.2rem' }} />
                      <img src={hitpayLogo} alt='HitPay' className='hitpay-loading-logo' />
                    </div>
                    <h4 className='hitpay-status-title'>
                      {isRedirecting ? 'Redirecting to HitPay...' : 'Preparing Secure Payment...'}
                    </h4>
                    <p className='hitpay-status-desc'>
                      {isRedirecting
                        ? 'Please wait, taking you to the HitPay checkout screen...'
                        : 'Setting up your secure transaction with HitPay.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {hitpayUrl && !isPayTogether ? (
                      <a
                        href={hitpayUrl}
                        className='hitpay-btn-checkout'
                        onClick={() => setIsRedirecting(true)}
                      >
                        <div className='hitpay-btn-content'>
                          <div className='hitpay-btn-logo-circle'>
                            <img src={hitpayLogo} alt='HitPay' />
                          </div>
                          <div className='hitpay-btn-text'>
                            <span className='hitpay-btn-title'>Pay with HitPay</span>
                            <span className='hitpay-btn-subtitle'>
                              Fast & secure online checkout
                            </span>
                          </div>
                        </div>
                        <div className='hitpay-btn-arrow'>
                          <FiArrowRight size={18} />
                        </div>
                      </a>
                    ) : isPayTogether ? (
                      <button
                        type='button'
                        onClick={async () => {
                          setIsRedirecting(true)
                          const url = await fetchHitpayQr()
                          if (url) {
                            window.location.href = url
                          } else {
                            setIsRedirecting(false)
                          }
                        }}
                        className='hitpay-btn-checkout'
                      >
                        <div className='hitpay-btn-content'>
                          <div className='hitpay-btn-logo-circle'>
                            <img src={hitpayLogo} alt='HitPay' />
                          </div>
                          <div className='hitpay-btn-text'>
                            <span className='hitpay-btn-title'>Pay Together with HitPay</span>
                            <span className='hitpay-btn-subtitle'>
                              Pay {listCheckedOrder?.length ? `${listCheckedOrder.length} orders` : 'all orders'} in one go
                            </span>
                          </div>
                        </div>
                        <div className='hitpay-btn-arrow'>
                          <FiArrowRight size={18} />
                        </div>
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            ) : (
              <div className='detail-list-news detail-list-news--org'>
                {isPaymentViaHitPay && isHitpayFallback && (
                  <div className='hitpay-fallback-notice'>
                    <div className='hitpay-fallback-icon'>
                      <FiAlertCircle size={22} />
                    </div>
                    <div className='hitpay-fallback-body'>
                      <div className='hitpay-fallback-title'>
                        HitPay gateway is currently unavailable
                      </div>
                      <p className='hitpay-fallback-text'>
                        We could not connect to HitPay online gateway. You can complete
                        your order using the direct PayNow QR below.
                      </p>
                      <button
                        type='button'
                        className='hitpay-fallback-retry-btn'
                        onClick={() => fetchHitpayQr()}
                      >
                        <FiRefreshCw size={12} />
                        Retry HitPay
                      </button>
                    </div>
                  </div>
                )}
                <h3 className='c-ttl-blue text-center' style={{ fontSize: '20px' }}>
                  <strong>Step 1: </strong>
                  Please scan the following QR code to pay
                  <span style={{ color: '#51aaaa' }}>
                    {' '}
                    $<span id='amount2'>{final?.toFixed(2)} </span>
                  </span>
                  to
                  <span id='sellerPhone' style={{ color: '#51aaaa' }}>
                    {' '}
                    {host?.isPayWithUEN ? host.uen : phone}
                  </span>
                </h3>
                <p className='text-center' style={{ fontSize: '20px' }}>
                  Add your unique reference code <b id='orderRef'> {stringRefNumber}</b>
                </p>
                <div className='qr-box text-center' style={{ position: 'relative' }}>
                  {shouldShowQrOverlay ? (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.82)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        zIndex: 1,
                      }}
                    >
                      <Spinner style={{ color: '#15cdca' }} />
                      <span style={{ color: '#51aaaa', fontWeight: 600 }}>Updating QR code...</span>
                    </div>
                  ) : null}
                  <div
                    id='qrcode'
                    style={{ width: '256px', margin: 'auto', opacity: shouldShowQrOverlay ? 0.35 : 1 }}
                    title={payNowCode}
                  >
                    <canvas width='256' height='256' style={{ display: 'none' }}></canvas>
                    <img src={src} style={{ display: 'block' }} width='256' height='256' />
                  </div>
                  <div
                    className={'d-flex align-items-center justify-content-between mt-3'}
                    style={{ opacity: shouldShowQrOverlay ? 0.55 : 1 }}
                  >
                    <button
                      onClick={onDownloadQRCode}
                      style={{ backgroundColor: 'transparent', border: 'none', color: '#15cdca' }}
                      disabled={shouldShowQrOverlay}
                    >
                      <AiOutlineCloudDownload className={'me-1'} />
                      Download QRCode
                    </button>
                    <button
                      onClick={onReloadQRCode}
                      style={{ backgroundColor: 'transparent', border: 'none', color: '#939091' }}
                      disabled={shouldShowQrOverlay}
                    >
                      Click to reload QRCode
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!isShowHitpay && (
            <>
              <h3 className='c-ttl-blue text-center'>
                <strong>Step 2: </strong>
                Attach PayNow screenshot (Do not attach SMS)
              </h3>
              <div className='c-form mb-4'>
                <div className='upload-image upload-screenshot'>
                  <img
                    style={
                      photoUrl
                        ? { height: '100%', width: '100%', objectFit: 'contain' }
                        : { display: 'none' }
                    }
                    id='eventImageImg'
                    src={photoUrl}
                    alt=''
                  />
                  <div className='upload-image-txt' style={photoUrl ? { opacity: 0 } : {}}>
                    <i className='icon-capture'></i>
                    <span>Screenshot</span>
                  </div>
                  <div className='u-file-upload__item'>
                    <div className='u-file-upload__wrap'>
                      <a className='c-upload preview-images-zone'>
                        <input
                          type='file'
                          accept='image/*'
                          size='40'
                          className='inputFile'
                          onChange={onUserImageUploaded}
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <button className='btn btn--main' onClick={confirmPayment} disabled={isSubmittedPayment}>
                {isSubmittedPayment ? <Spinner color='light' size='sm' /> : 'CONFIRM PAYMENT'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

Payment.displayName = 'Payment'
export default Payment

Payment.propTypes = {
  confirmPayment: PropTypes.func,
  phone: PropTypes.string,
  paymentFileUrl: PropTypes.string,
  final: PropTypes.number,
  orderRef: PropTypes.string,
  orderNo: PropTypes.number,
  pickupTime: PropTypes.string,
  isSubmittedPayment: PropTypes.bool,
  isQrLoading: PropTypes.bool,
  isPaymentViaHitPay: PropTypes.bool,
  listCheckedOrder: PropTypes.array,
}
